import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';

export interface SystemLogEntry {
  id: string;
  source_type: string;
  source_name: string;
  namespace: string;
  container_name: string | null;
  log_level: string | null;
  message: string;
  metadata: Record<string, unknown>;
  event_type: string | null;
  event_reason: string | null;
  occurred_at: string;
  ingested_at: string;
}

export interface SystemLogsPagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface SystemLogsResponse {
  data: SystemLogEntry[];
  pagination: SystemLogsPagination;
}

export interface SystemLogsSummary {
  summary: {
    total_entries: number;
    error_count: number;
    fatal_count: number;
    warning_count: number;
    info_count: number;
    debug_count: number;
    unique_sources: number;
    earliest: string | null;
    latest: string | null;
  };
  bySourceType: Array<{ source_type: string; count: number }>;
}

export interface SystemLogsFilters {
  limit?: number;
  offset?: number;
  source_type?: string;
  source_name?: string;
  namespace?: string;
  log_level?: string;
  event_type?: string;
  search?: string;
  from?: string;
  to?: string;
}

/**
 * Fetches paginated system logs with filters.
 * Requires admin role.
 */
export const useSystemLogs = (filters: SystemLogsFilters = {}) => {
  const { isAuthenticated, isAdmin } = useAuth();

  const params = new URLSearchParams();
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  if (filters.source_type) params.set('source_type', filters.source_type);
  if (filters.source_name) params.set('source_name', filters.source_name);
  if (filters.namespace) params.set('namespace', filters.namespace);
  if (filters.log_level) params.set('log_level', filters.log_level);
  if (filters.event_type) params.set('event_type', filters.event_type);
  if (filters.search) params.set('search', filters.search);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);

  const queryString = params.toString();
  const url = queryString
    ? `${API_CONFIG.ENDPOINTS.SYSTEM_LOGS}?${queryString}`
    : API_CONFIG.ENDPOINTS.SYSTEM_LOGS;

  return useQuery<SystemLogsResponse>({
    queryKey: queryKeys.systemLogs.list(filters as Record<string, unknown>),
    queryFn: () => httpClient.get<SystemLogsResponse>(url),
    enabled: isAuthenticated && isAdmin,
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
};

/**
 * Fetches system logs summary (aggregated counts matching current filters).
 * Requires admin role.
 */
export const useSystemLogsSummary = (filters: SystemLogsFilters = {}) => {
  const { isAuthenticated, isAdmin } = useAuth();

  const params = new URLSearchParams();
  if (filters.source_type) params.set('source_type', filters.source_type);
  if (filters.source_name) params.set('source_name', filters.source_name);
  if (filters.namespace) params.set('namespace', filters.namespace);
  if (filters.log_level) params.set('log_level', filters.log_level);
  if (filters.event_type) params.set('event_type', filters.event_type);
  if (filters.search) params.set('search', filters.search);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);

  const queryString = params.toString();
  const url = queryString
    ? `${API_CONFIG.ENDPOINTS.SYSTEM_LOGS_SUMMARY}?${queryString}`
    : API_CONFIG.ENDPOINTS.SYSTEM_LOGS_SUMMARY;

  return useQuery<SystemLogsSummary>({
    queryKey: queryKeys.systemLogs.summary(filters as Record<string, unknown>),
    queryFn: () => httpClient.get<SystemLogsSummary>(url),
    enabled: isAuthenticated && isAdmin,
    refetchInterval: 60_000,
    staleTime: 10_000,
  });
};
